import { galleryItemHref } from "@/eln/gallery/components/GalleryItemLink";
import i18n from "../../../../modules/common/i18n";
import type { RecordIconData } from "../../../../stores/definitions/BaseRecord";

import { GLOBAL_ID_PATTERN } from "./linkTarget";

export const INVENTORY_PREFIX_ICON_DATA: Record<string, RecordIconData> = {
  SA: {
    iconName: "sample",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.sample.singular");
    },
  },
  SS: {
    iconName: "subsample",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.subsample.singular");
    },
  },
  IC: {
    iconName: "container",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.container.singular");
    },
  },
  IN: {
    iconName: "instrument",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.instrument.singular");
    },
  },
  IT: {
    iconName: "template",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.sampleTemplate.singular");
    },
  },
  NT: {
    iconName: "instrumentTemplate",
    get recordTypeLabel() {
      return i18n.t("inventory:recordTypes.instrumentTemplate.singular");
    },
  },
};

// RecordTypeIcon supports "document" and "gallery" but has no dedicated notebook
// icon, so notebooks reuse the document icon (with a Notebook label).
const ELN_PREFIX_ICON_DATA: Record<string, RecordIconData> = {
  SD: {
    iconName: "document",
    get recordTypeLabel() {
      return i18n.t("common:recordTypes.document.singular");
    },
  },
  NB: {
    iconName: "document",
    get recordTypeLabel() {
      return i18n.t("common:recordTypes.notebook.singular");
    },
  },
  GL: {
    iconName: "gallery",
    get recordTypeLabel() {
      return i18n.t("common:recordTypes.galleryFile.singular");
    },
  },
};

/** Returns the two-letter prefix of a Global ID (ignoring any version suffix), or null. */
export function prefixOf(globalId: string): string | null {
  const match = GLOBAL_ID_PATTERN.exec(globalId);
  return match ? match[1] : null;
}

/**
 * Returns the RecordTypeIcon data appropriate for the supplied Inventory Global ID, or null when
 * the prefix is not an inventory item.
 */
export function iconForInventoryGlobalId(globalId: string): RecordIconData | null {
  const prefix = prefixOf(globalId);
  return prefix ? (INVENTORY_PREFIX_ICON_DATA[prefix] ?? null) : null;
}

/**
 * Returns RecordTypeIcon data for any supported link target (Inventory item or ELN document,
 * notebook or gallery file), or null when the prefix is not a supported target. Only ever returns
 * iconName values that RecordTypeIcon knows about, since that component throws on unknown names.
 */
export function iconForGlobalId(globalId: string): RecordIconData | null {
  const prefix = prefixOf(globalId);
  if (!prefix) return null;
  return INVENTORY_PREFIX_ICON_DATA[prefix] ?? ELN_PREFIX_ICON_DATA[prefix] ?? null;
}

/**
 * True when the Global ID is an Inventory item (sample, subsample, container, instrument,
 * sample/instrument template).
 */
export function isInventoryGlobalId(globalId: string): boolean {
  const prefix = prefixOf(globalId);
  return prefix != null && prefix in INVENTORY_PREFIX_ICON_DATA;
}

/**
 * Targets that support version pinning in the link UI: Inventory items, ELN documents (SD) and
 * gallery files (GL, since RSDEV-1250 gave them a revision history). Notebooks (NB) do not, so the
 * version-pin affordance must not be rendered for them.
 */
export function supportsVersionPin(globalId: string): boolean {
  const prefix = prefixOf(globalId);
  return isInventoryGlobalId(globalId) || prefix === "SD" || prefix === "GL";
}

/**
 * URL to OPEN a link target in a new tab. A Gallery file (GL) opens at its
 * location in the Gallery (/gallery/item/<id>[/<version>]) rather than downloading
 * via /globalId, which is the misleading default the backend resolves to Streamfile.
 * Every other target keeps the existing /globalId/<gid>[v<n>] behaviour, which
 * the backend resolves per type (e.g. SD documents, inventory items).
 */
export function openUrlForTarget(globalId: string, versionPin: number | null): string {
  const match = GLOBAL_ID_PATTERN.exec(globalId);
  if (match && match[1] === "GL") {
    return galleryItemHref(match[2], versionPin ?? undefined);
  }
  // rebuild from the parsed base id: rows written before the backend
  // normalised stored ids can already carry a "vN" suffix, which must not be
  // doubly encoded (e.g. /globalId/SS42v7v7)
  const base = match ? `${match[1]}${match[2]}` : globalId;
  const versionSuffix = versionPin != null ? `v${versionPin}` : "";
  return `/globalId/${base}${versionSuffix}`;
}
